import { FormControl, FormGroup, Validators } from '@angular/forms';

import { predictedFileNamePattern } from '../../../ui/form/validation-pattern';
import { ExportFileConfigurationEntity } from '../export-file-configuration/export-file-configuration.entity';
import { ExportPartitionConfigurationEntity } from '../export-partition-configuration/export-partition-configuration.entity';
import { ExportWriteModeConfigurationEntity } from '../export-write-mode-configuration/export-write-mode-configuration.entity';
import {
	ConnectorMode,
	ExportConnectorEntity,
} from './export-connector.entity';
import {
	ExportFormat,
	ExportTemplateDetailsEntity,
} from './export-template-details.entity';
import type { ExportTemplateDetailsForm } from './export-template-details.form';
import { ExportTemplateFormControls } from './export-template-form-controls';

export abstract class ExportConfigFormHelper {
	public generate(id: number, form: FormGroup): ExportTemplateDetailsEntity {
		const commonConfig = this.buildCommonConfig(id, form);
		return this.buildCompletePayload(commonConfig, form);
	}

	public populate(
		config: ExportTemplateDetailsEntity,
		form: ExportTemplateDetailsForm
	): void {
		const isDb =
			config.connector.type === ConnectorMode.SQL ||
			config.connector.type === ConnectorMode.NoSQL ||
			config.connector.type === ConnectorMode.Neo4j;

		if (!isDb) {
			this.commonPopulate(config, form);
		}
		this.populateWithConfig(config, form);
	}

	public update(
		connector: ExportConnectorEntity,
		exportType: ExportFormat,
		form: ExportTemplateDetailsForm
	): void {
		const isDb =
			connector.type === ConnectorMode.SQL ||
			connector.type === ConnectorMode.NoSQL ||
			connector.type === ConnectorMode.Neo4j;

		if (!isDb) {
			this.commonUpdates(form);
		}
		this.updateWithConfig(connector, exportType, form);
	}

	protected abstract buildCompletePayload(
		commonConfig: ExportTemplateDetailsEntity,
		form: FormGroup
	): ExportTemplateDetailsEntity;

	protected abstract populateWithConfig(
		entity: ExportTemplateDetailsEntity,
		form: ExportTemplateDetailsForm
	): void;

	protected abstract updateWithConfig(
		connector: ExportConnectorEntity,
		exportType: ExportFormat,
		form: ExportTemplateDetailsForm
	): void;

	private buildCommonConfig(
		id: number,
		form: FormGroup
	): ExportTemplateDetailsEntity {
		return ExportTemplateDetailsEntity.build({
			id,
			connector:
				form.controls[ExportTemplateFormControls.ConnectorMode].value ??
				undefined,
			outputType:
				form.controls[ExportTemplateFormControls.ExportType].value ?? undefined,
			keepOriginalLabels:
				form.controls[ExportTemplateFormControls.KeepOriginalLabels].value ??
				undefined,
			compress:
				form.controls[ExportTemplateFormControls.Compress]?.value ?? undefined,
			path: form.controls[ExportTemplateFormControls.Path]?.value ?? undefined,
			partition:
				(form.controls[ExportTemplateFormControls.Partitioning]
					?.value as ExportPartitionConfigurationEntity) ?? undefined,
			fileNameConfig:
				(form.controls[ExportTemplateFormControls.FileConfig]
					?.value as ExportFileConfigurationEntity) ?? undefined,
			writeMode:
				(form.controls[ExportTemplateFormControls.WriteModeConfig]
					?.value as ExportWriteModeConfigurationEntity) ?? undefined,
		});
	}

	protected ensureControl<T>(
		form: FormGroup,
		name: ExportTemplateFormControls,
		create: () => FormControl<T>
	): FormControl<T> {
		const existing = form.get(name) as FormControl<T> | null;
		if (existing !== null) {
			return existing;
		}
		const created = create();
		form.addControl(name, created);
		return created;
	}

	private commonPopulate(
		config: ExportTemplateDetailsEntity,
		exportTemplateForm: ExportTemplateDetailsForm
	): void {
		exportTemplateForm.partition = this.ensureControl(
			exportTemplateForm.form,
			ExportTemplateFormControls.Partitioning,
			() => new FormControl(ExportPartitionConfigurationEntity.build())
		);

		exportTemplateForm.fileConfig = this.ensureControl(
			exportTemplateForm.form,
			ExportTemplateFormControls.FileConfig,
			() => new FormControl(ExportFileConfigurationEntity.build())
		);

		exportTemplateForm.compress = this.ensureControl(
			exportTemplateForm.form,
			ExportTemplateFormControls.Compress,
			() => new FormControl(false)
		);

		exportTemplateForm.fileNamePatternPrediction = this.ensureControl(
			exportTemplateForm.form,
			ExportTemplateFormControls.FileNamePatternPrediction,
			() =>
				new FormControl<string>('', [
					Validators.pattern(predictedFileNamePattern),
				])
		);

		// Applied on every populate, not just when the control is created: the
		// control outlives a populate, and the first one runs against the store
		// defaults where predicted is still empty.
		if (config.fileNameConfig.predicted !== '') {
			exportTemplateForm.seedPredictedFileName(config.fileNameConfig.predicted);
		}

		exportTemplateForm.toggleControls(
			true,
			ExportTemplateFormControls.ExportType,
			ExportTemplateFormControls.FileConfig,
			ExportTemplateFormControls.Partitioning,
			ExportTemplateFormControls.KeepOriginalLabels,
			ExportTemplateFormControls.Compress,
			ExportTemplateFormControls.FileNamePatternPrediction
		);
	}

	private commonUpdates(exportForm: ExportTemplateDetailsForm): void {
		exportForm.partition = this.ensureControl(
			exportForm.form,
			ExportTemplateFormControls.Partitioning,
			() => new FormControl(ExportPartitionConfigurationEntity.build())
		);

		exportForm.fileConfig = this.ensureControl(
			exportForm.form,
			ExportTemplateFormControls.FileConfig,
			() => new FormControl(ExportFileConfigurationEntity.build())
		);

		this.ensureControl(
			exportForm.form,
			ExportTemplateFormControls.Path,
			() => new FormControl<string>('/')
		);

		exportForm.compress = this.ensureControl(
			exportForm.form,
			ExportTemplateFormControls.Compress,
			() => new FormControl<boolean>(false)
		);

		exportForm.fileNamePatternPrediction = this.ensureControl(
			exportForm.form,
			ExportTemplateFormControls.FileNamePatternPrediction,
			() =>
				new FormControl<string>('', [
					Validators.pattern(predictedFileNamePattern),
				])
		);

		exportForm.toggleControls(
			true,
			ExportTemplateFormControls.ExportType,
			ExportTemplateFormControls.FileConfig,
			ExportTemplateFormControls.Partitioning,
			ExportTemplateFormControls.Path,
			ExportTemplateFormControls.KeepOriginalLabels,
			ExportTemplateFormControls.Compress,
			ExportTemplateFormControls.FileNamePatternPrediction
		);
	}
}
